import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {AsyncLoader} from '../../../../../components/common/loaders/asyncLoader';
import {useLinkNavigation} from '../../../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../../../components/common/skeleton';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {
  LocalApplicationServerAuthenticationConfigModel,
  LocalApplicationServerAuthenticationStrategiesEnum,
  LocalApplicationServerModel,
} from '../../../../../models/api/localApplicationServerModel';
import {buildSettingsDevelopersPath} from '../../../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersApplicationServersUrl} from '../../../../../models/url/settingsUrlModel';
import {
  createLocalApplicationServerAsyncAction,
  fetchLocalApplicationServersAsyncAction,
  updateLocalApplicationServerAsyncAction,
} from '../../../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../banners/settingsBannersContext';
import {SettingsDevelopersApplicationCreateEditServer} from './settingsDevelopersApplicationCreateEditServerForm';

/*
 * Constants.
 */

const defaultAuthConfig: LocalApplicationServerAuthenticationConfigModel = {
  type: LocalApplicationServerAuthenticationStrategiesEnum.NONE,
};

/*
 * Props.
 */

interface SettingsDevelopersApplicationConnectPageProps {
  url: SettingsDevelopersApplicationServersUrl;
  server?: LocalApplicationServerModel;
  servers?: ReadonlyArray<LocalApplicationServerModel>;
  serversAsyncStatus?: AsyncStatusesEnum;
}

/*
 * Style.
 */

const StyledFormWrapperDiv = styled.div`
  max-width: 600px;
  width: 100%;
`;

const StyledFooterDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.alphas.gray10};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 8px;
  background: ${(p) => p.theme.greys.white};
`;

const StyledSettingsDevelopersApplicationCreateEditServer = styled(
  SettingsDevelopersApplicationCreateEditServer,
)`
  padding-bottom: 64px;
`;

const StyledErrorWrapperDiv = styled.div`
  flex: 1;
  padding: 32px;
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationConnectPage: FC<SettingsDevelopersApplicationConnectPageProps> = (
  props,
) => {
  const {url, server, servers, serversAsyncStatus} = props;
  const [origin, setOrigin] = useState(server?.origin ?? '');
  const [isFormValid, setIsFormValid] = useState(false);
  const [authConfig, setAuthConfig] = useState<LocalApplicationServerAuthenticationConfigModel>(
    server?.authenticationConfig ?? defaultAuthConfig,
  );

  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const {showBanner} = useSettingsBanners();

  useInitialEffect(() => {
    dispatch(fetchLocalApplicationServersAsyncAction({localApplicationId: url.applicationId}));
  });

  useEffect(() => {
    setOrigin(server?.origin ?? '');
    setAuthConfig(server?.authenticationConfig ?? defaultAuthConfig);
  }, [server]);

  const onConnectAsync = async () => {
    try {
      await dispatch(createLocalApplicationServerAsyncAction(url.applicationId, origin, authConfig));
      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: <Trans id="developers-application-server-success-message">Server connected</Trans>,
      });
      navigateToLink(
        buildSettingsDevelopersPath({
          ...url,
          isConnecting: false,
        }),
      );
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="developers-application-server-error-message">
            Error connecting server. Please try again.
          </Trans>
        ),
      });
    }
  };

  const onUpdateAsync = async (serverId: number) => {
    try {
      await dispatch(
        updateLocalApplicationServerAsyncAction(url.applicationId, serverId, origin, authConfig),
      );
      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: <Trans id="developers-application-server-update-success-message">Server updated</Trans>,
      });
      navigateToLink(
        buildSettingsDevelopersPath({
          ...url,
          serverId: undefined,
        }),
      );
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="developers-application-server-update-error-message">
            Error updating server. Please try again.
          </Trans>
        ),
      });
    }
  };

  // TODO: Flesh out a better loading state.
  if (!servers || serversAsyncStatus === AsyncStatusesEnum.LOADING) {
    return <Skeleton height="300px" width="600px" />;
  }
  // If we failed to fetch the servers.
  if (serversAsyncStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.FAILED}
          onRetryClick={() =>
            dispatch(fetchLocalApplicationServersAsyncAction({localApplicationId: url.applicationId}))
          }
        />
      </StyledErrorWrapperDiv>
    );
  }

  return (
    <StyledFormWrapperDiv>
      <StyledSettingsDevelopersApplicationCreateEditServer
        origin={origin}
        authConfig={authConfig}
        servers={servers}
        currentServer={server}
        onAuthConfigChange={setAuthConfig}
        onOriginChange={setOrigin}
        onFormValidChange={setIsFormValid}
      />
      <StyledFooterDiv>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button
            onClick={() =>
              navigateToLink(
                buildSettingsDevelopersPath({
                  ...url,
                  isConnecting: false,
                  serverId: undefined,
                }),
              )
            }
          >
            <Trans id="developers-application-server-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!isFormValid}
            onClick={() => (server ? onUpdateAsync(server.id) : onConnectAsync())}
          >
            <Trans id="developers-application-server-connect-button">Connect</Trans>
          </Button>
        </ButtonGroup>
      </StyledFooterDiv>
    </StyledFormWrapperDiv>
  );
};
